import React, { useEffect, useMemo, useState } from "react";
import { ReactSortable } from "react-sortablejs";
import PropTypes from "prop-types";

import { Button, Container, Header, Loader } from "semantic-ui-react";

import { useFeatures } from "components/FeatureFlag";
import SimpleLinePlaceholder from "components/SimpleLinePlaceholder";
import SimpleList from "components/SimpleList";

import { useAccessTypeStore } from "scenes/Hubly/components/Workspace/components/hooks";
import {
  useDragDropContext,
  useDragDropEventManager,
  WithDragDropContext,
} from "scenes/Hubly/components/Workspace/components/WorkflowContainer/components/WorkflowEditor/hooks";

import { useNextWorkflow } from "./hooks";
import NextWorkflowConditionsGroupView from "./components/NextWorkflowConditionsGroupView";
import {
  NextWorkflowAdd,
  NextWorkflowConditionsLauncher,
  NextWorkflowItem,
} from "./components";

export function NextWorkflowsEditor({ workflow }) {
  const { enabled: enableConditionalNextWf } = useFeatures(
    "conditional-next-workflows"
  );
  const { hasAccess } = useAccessTypeStore();
  const [addingNextWf, setAddingNextWf] = useState(false);
  const {
    loading,
    dndInProgress,
    reorder,
    updateLoader,
    nextWorkflows,
    reloadNextWorkflow,
    createNextWorkflow,
    removeNextWorkflow,
    editNextWorkflow,
  } = useNextWorkflow(
    useMemo(() => {
      return workflow;
    }, [workflow])
  );

  const {
    itemList: nextWorkflowList,
    modifyRootList,
    modifySubList,
    setDraggedItem,
  } = useDragDropEventManager(nextWorkflows, reorder, "nextWorkflow");
  const [, dispatch] = useDragDropContext();

  useEffect(() => {
    dispatch({ type: "set-disabled", value: dndInProgress });
  }, [dndInProgress, dispatch]);

  if (loading && nextWorkflows.length === 0) {
    return (
      <Container style={{ marginTop: "1em" }}>
        <Header as="h5">Next Workflows</Header>
        <SimpleLinePlaceholder />
      </Container>
    );
  }

  return (
    <Container className="next_workflows" style={{ marginTop: "1em" }}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Header as="h5" style={{ marginBottom: "0.5em" }}>
          Next Workflows
        </Header>
        <Loader
          size="tiny"
          inline
          active={dndInProgress}
          style={{ marginRight: "0.3em" }}
        />
      </div>
      <SimpleList.Container>
        {nextWorkflowList.length === 0 && (
          <SimpleList.Empty>No workflows queued</SimpleList.Empty>
        )}

        {enableConditionalNextWf ? (
          <ReactSortable
            list={nextWorkflowList}
            disabled={!hasAccess() || dndInProgress}
            setList={modifyRootList}
            group={`nextWorkflowGroups-${workflow.id}`}
            ghostClass="hubly-dnd-ghost"
            animation={100}
            handle=".nextWorkflow-drag-handle"
            onStart={(e) => {
              setDraggedItem(nextWorkflowList[e.oldIndex]);
              dispatch({ type: "set-dragging", value: true });
            }}
            onEnd={() => {
              dispatch({ type: "set-dragging", value: false });
            }}
            forceFallback
          >
            {nextWorkflowList.map((nwfGroup, i) => {
              return (
                <NextWorkflowConditionsGroupView
                  key={nwfGroup.id}
                  nextWorkflowGroup={nwfGroup}
                  workflowId={workflow.id}
                  disabled={!hasAccess() || !enableConditionalNextWf}
                  onConfigChange={reloadNextWorkflow}
                  onOrderModified={(updatedList) => {
                    modifySubList(i, nwfGroup, updatedList);
                  }}
                  onDragged={(isDragging, e) => {
                    if (isDragging)
                      setDraggedItem(nwfGroup?.nextWorkflow?.[e.oldIndex]);

                    dispatch({ type: "set-dragging", value: isDragging });
                  }}
                >
                  {(nwfList) => {
                    return nwfList.map((nwf) => {
                      const { id, nextWorkflow, beingDeleted = false } = nwf;
                      return (
                        <NextWorkflowItem
                          key={id}
                          nextWorkflow={nextWorkflow}
                          beingDeleted={beingDeleted}
                          disabled={!hasAccess()}
                          enableDnd={enableConditionalNextWf}
                          options={
                            !nwfGroup?.conditions?.length &&
                            enableConditionalNextWf && (
                              <NextWorkflowConditionsLauncher
                                workflowId={workflow.id}
                                item={nwf}
                                onConfigChange={editNextWorkflow}
                              />
                            )
                          }
                          onDelete={() => {
                            removeNextWorkflow(nwf);
                          }}
                        />
                      );
                    });
                  }}
                </NextWorkflowConditionsGroupView>
              );
            })}
          </ReactSortable>
        ) : (
          nextWorkflowList
            .filter((nwfGroup) => {
              return !nwfGroup?.conditions?.length;
            })
            .map((nwfGroup) => {
              const { id, nextWorkflow, beingDeleted = false } = nwfGroup;

              return (
                <NextWorkflowItem
                  key={id}
                  nextWorkflow={nextWorkflow}
                  beingDeleted={beingDeleted}
                  disabled={!hasAccess()}
                  enableDnd={false}
                  onDelete={() => {
                    removeNextWorkflow(nwfGroup);
                  }}
                />
              );
            })
        )}
      </SimpleList.Container>
      {!addingNextWf && hasAccess() && (
        <Button
          disabled={updateLoader}
          style={{ marginTop: "0.5em" }}
          basic
          onClick={() => {
            setAddingNextWf(true);
          }}
        >
          Add Workflow
        </Button>
      )}
      {addingNextWf && (
        <NextWorkflowAdd
          disabled={updateLoader}
          enableConditions={enableConditionalNextWf}
          workflow={workflow}
          onAdd={createNextWorkflow}
          onClose={() => {
            setAddingNextWf(false);
          }}
        />
      )}
    </Container>
  );
}

NextWorkflowsEditor.propTypes = {
  workflow: PropTypes.object,
};

NextWorkflowsEditor.defaultProps = {
  workflow: null,
};

export default WithDragDropContext(NextWorkflowsEditor);
